(() => {
  if (window.SilverBullet?.fitment) return;

  const STORAGE_KEY = 'silverBulletVehicleFitment';
  const URL_KEYS = ['year', 'make', 'model', 'bed'];
  const VALID_STATE_KEYS = [
    'version',
    'fitmentKey',
    'productHandle',
    'make',
    'makeKey',
    'model',
    'modelKey',
    'year',
    'bed',
    'bedKey',
    'variantId',
    'source',
    'savedAt',
  ];

  const readCatalog = () => {
    const catalogNode = document.querySelector('[data-sb-fitment-catalog]');
    if (!catalogNode) return null;

    try {
      const catalog = JSON.parse(catalogNode.textContent);
      if (!catalog?.version || !Array.isArray(catalog.entries)) return null;
      return catalog;
    } catch (error) {
      console.warn('Silver Bullet fitment catalog could not be read.', error);
      return null;
    }
  };

  const catalog = readCatalog();
  if (!catalog) return;

  const normalizeKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const numberOrNull = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const variantIdString = (value) => {
    const normalized = String(value || '').replace('gid://shopify/ProductVariant/', '');
    return /^\d+$/.test(normalized) ? normalized : '';
  };

  const publish = (name, payload = {}) => {
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    );

    if (window.SilverBulletEvents?.publish) {
      window.SilverBulletEvents.publish(name, cleanPayload);
      return;
    }

    document.dispatchEvent(new CustomEvent(name, { detail: cleanPayload }));
    try {
      window.Shopify?.analytics?.publish?.(name, cleanPayload);
    } catch (error) {
      console.debug('Silver Bullet event publisher is unavailable.', error);
    }
  };

  const loadStoredState = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!stored || stored.version !== catalog.version) return {};
      return Object.fromEntries(
        VALID_STATE_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(stored, key)).map((key) => [
          key,
          stored[key],
        ])
      );
    } catch (error) {
      return {};
    }
  };

  const saveState = (nextState) => {
    const safeState = Object.fromEntries(
      VALID_STATE_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(nextState, key)).map((key) => [
        key,
        nextState[key],
      ])
    );
    safeState.version = catalog.version;
    safeState.savedAt = Date.now();

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safeState));
    } catch (error) {
      // Fitment remains functional when session storage is unavailable.
    }

    return safeState;
  };

  const urlState = () => {
    const parameters = new URLSearchParams(window.location.search);
    return {
      year: numberOrNull(parameters.get('year')),
      make: parameters.get('make') || '',
      makeKey: normalizeKey(parameters.get('make')),
      model: parameters.get('model') || '',
      modelKey: normalizeKey(parameters.get('model')),
      bed: parameters.get('bed') || '',
      bedKey: normalizeKey(parameters.get('bed')),
      variantId: variantIdString(parameters.get('variant')),
    };
  };

  const entrySupportsYear = (entry, year) =>
    Number.isInteger(year) &&
    entry.beds.some((bed) => year >= Number(bed.yearMin) && year <= Number(bed.yearMax));

  const findEntry = (candidate = {}) => {
    const makeKey = candidate.makeKey || normalizeKey(candidate.make);
    const modelKey = candidate.modelKey || normalizeKey(candidate.model);
    const variantId = variantIdString(candidate.variantId);
    const productEntries = candidate.productHandle
      ? catalog.entries.filter((entry) => entry.productHandle === candidate.productHandle)
      : [];
    const variantEntries = catalog.entries.filter(
      (entry) =>
        variantId &&
        (!candidate.productHandle || entry.productHandle === candidate.productHandle) &&
        entry.beds.some((bed) => variantIdString(bed.variantId) === variantId)
    );

    return (
      catalog.entries.find(
        (entry) =>
          candidate.fitmentKey &&
          entry.fitmentKey === candidate.fitmentKey &&
          (!candidate.productHandle || entry.productHandle === candidate.productHandle) &&
          (!makeKey || entry.makeKey === makeKey) &&
          (!modelKey || entry.modelKey === modelKey)
      ) ||
      catalog.entries.find(
        (entry) =>
          candidate.productHandle &&
          entry.productHandle === candidate.productHandle &&
          (!makeKey || entry.makeKey === makeKey) &&
          (!modelKey || entry.modelKey === modelKey)
      ) ||
      catalog.entries.find(
        (entry) =>
          !candidate.productHandle &&
          makeKey &&
          modelKey &&
          entry.makeKey === makeKey &&
          entry.modelKey === modelKey
      ) ||
      (variantEntries.length === 1 ? variantEntries[0] : null) ||
      (productEntries.length === 1 ? productEntries[0] : null) ||
      null
    );
  };

  const findBed = (entry, candidate = {}) => {
    if (!entry) return null;
    const variantId = variantIdString(candidate.variantId);
    const bedKey = candidate.bedKey || normalizeKey(candidate.bed);
    const year = numberOrNull(candidate.year);

    return (
      entry.beds.find((bed) => variantId && variantIdString(bed.variantId) === variantId) ||
      entry.beds.find(
        (bed) =>
          bedKey &&
          bed.bedKey === bedKey &&
          (!Number.isInteger(year) ||
            (year >= Number(bed.yearMin) && year <= Number(bed.yearMax)))
      ) ||
      null
    );
  };

  const normalizeState = (candidate = {}) => {
    const entry = findEntry(candidate);
    const bed = findBed(entry, candidate);
    const year = numberOrNull(candidate.year);
    const yearIsValid =
      Number.isInteger(year) &&
      year >= 1900 &&
      year <= Number(catalog.approvedYearMax) &&
      (!entry ||
        (year >= Number(entry.yearMin) &&
          year <= Number(entry.yearMax) &&
          (!bed || (year >= Number(bed.yearMin) && year <= Number(bed.yearMax)))));

    return {
      version: catalog.version,
      fitmentKey: entry?.fitmentKey || '',
      productHandle: entry?.productHandle || candidate.productHandle || '',
      make: entry?.make || candidate.make || '',
      makeKey: entry?.makeKey || normalizeKey(candidate.make),
      model: entry?.model || candidate.model || '',
      modelKey: entry?.modelKey || normalizeKey(candidate.model),
      year: yearIsValid ? year : null,
      bed: bed?.bed || candidate.bed || '',
      bedKey: bed?.bedKey || normalizeKey(candidate.bed),
      variantId: bed ? variantIdString(bed.variantId) : variantIdString(candidate.variantId),
      source: candidate.source || '',
    };
  };

  const productContext = () => {
    const productNode = document.querySelector('[data-sb-product-fitment]');
    if (!productNode) return {};
    return {
      productHandle: productNode.dataset.sbProductHandle || '',
      variantId:
        productNode.dataset.sbVariantId ||
        productNode.closest('product-information')?.querySelector('form input[name="id"]')?.value ||
        '',
    };
  };

  const storedState = loadStoredState();
  const explicitState = urlState();
  const currentProduct = productContext();
  const hasExplicitFitment = URL_KEYS.some((key) => new URLSearchParams(window.location.search).has(key));
  const storedStateMatchesProduct =
    currentProduct.productHandle && storedState.productHandle === currentProduct.productHandle;
  const initialState = currentProduct.productHandle
    ? {
        ...(storedStateMatchesProduct ? storedState : {}),
        ...(hasExplicitFitment ? explicitState : {}),
        ...currentProduct,
      }
    : hasExplicitFitment
      ? { ...storedState, ...explicitState }
      : storedState;
  let state = normalizeState(initialState);

  const updateState = (patch, shouldSave = true) => {
    state = normalizeState({ ...state, ...patch });
    if (patch.source) state.source = patch.source;
    if (shouldSave) state = { ...state, ...saveState(state) };
    return { ...state };
  };

  const fitmentUrl = (entry, bed, selectedYear) => {
    const destination = new URL(entry.productUrl, window.location.origin);
    destination.searchParams.set('variant', variantIdString(bed.variantId));
    destination.searchParams.set('year', String(selectedYear));
    destination.searchParams.set('make', entry.make);
    destination.searchParams.set('model', entry.model);
    destination.searchParams.set('bed', bed.bed);

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) destination.searchParams.set('preview_theme_id', previewThemeId);
    return `${destination.pathname}${destination.search}`;
  };

  const replaceFitmentParameters = (nextState = state) => {
    const entry = findEntry(nextState);
    const bed = findBed(entry, nextState);

    const currentUrl = new URL(window.location.href);
    if (nextState.year) currentUrl.searchParams.set('year', String(nextState.year));
    else currentUrl.searchParams.delete('year');
    if (entry?.make || nextState.make) currentUrl.searchParams.set('make', entry?.make || nextState.make);
    else currentUrl.searchParams.delete('make');
    if (entry?.model || nextState.model) currentUrl.searchParams.set('model', entry?.model || nextState.model);
    else currentUrl.searchParams.delete('model');
    if (bed?.bed || nextState.bed) currentUrl.searchParams.set('bed', bed?.bed || nextState.bed);
    else currentUrl.searchParams.delete('bed');
    if (bed?.variantId || nextState.variantId) {
      currentUrl.searchParams.set('variant', variantIdString(bed?.variantId || nextState.variantId));
    }
    else currentUrl.searchParams.delete('variant');
    window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

    const shareButton = document.querySelector('product-information share-button');
    shareButton?.updateUrl?.(currentUrl.toString());
  };

  const initializeSelector = (selector) => {
    if (selector.dataset.fitmentInitialized === 'true') return;

    const form = selector.querySelector('[data-fitment-form]');
    const yearField = selector.querySelector('[data-fitment-year]');
    const makeField = selector.querySelector('[data-fitment-make]');
    const modelField = selector.querySelector('[data-fitment-model]');
    const bedField = selector.querySelector('[data-fitment-bed]');
    const submitButton = selector.querySelector('[data-fitment-submit]');
    const status = selector.querySelector('[data-fitment-status]');
    const source = selector.dataset.fitmentSource || 'unknown';
    const updateUrl = selector.dataset.fitmentUpdateUrl === 'true';
    let started = false;

    if (!form || !yearField || !makeField || !modelField || !bedField || !submitButton) return;

    const approvedYearMin = Number(yearField.dataset.yearMin || catalog.approvedYearMin || 1900);
    const approvedYearMax = Number(yearField.dataset.yearMax || catalog.approvedYearMax);

    const setStatus = (message, kind = '') => {
      if (!status) return;
      status.textContent = message;
      status.dataset.status = kind;
    };

    const replaceOptions = (field, placeholder, options) => {
      const previousValue = field.value;
      field.replaceChildren(new Option(placeholder, ''));
      options.forEach(({ label, value, disabled = false }) => {
        const option = new Option(label, value);
        option.disabled = disabled;
        field.add(option);
      });
      if (options.some((option) => option.value === previousValue && !option.disabled)) {
        field.value = previousValue;
      }
    };

    const selectedYear = () => numberOrNull(yearField.value);
    const entriesForYear = () => catalog.entries.filter((entry) => entrySupportsYear(entry, selectedYear()));
    const selectedEntry = () =>
      catalog.entries.find(
        (entry) =>
          entrySupportsYear(entry, selectedYear()) &&
          entry.makeKey === makeField.value &&
          entry.modelKey === modelField.value
      ) || null;

    const refreshSubmitState = () => {
      const entry = selectedEntry();
      const bed = entry?.beds.find(
        (candidate) =>
          candidate.bedKey === bedField.value &&
          selectedYear() >= Number(candidate.yearMin) &&
          selectedYear() <= Number(candidate.yearMax)
      );
      submitButton.disabled = !entry || !bed || !bed.available;
      return { entry, bed };
    };

    const refreshBeds = (preferredBedKey = '') => {
      const entry = selectedEntry();
      const bedOptions = entry
        ? entry.beds
            .filter(
              (bed) =>
                selectedYear() >= Number(bed.yearMin) &&
                selectedYear() <= Number(bed.yearMax)
            )
            .map((bed) => ({
              label: bed.available ? bed.bed : `${bed.bed} — unavailable`,
              value: bed.bedKey,
              disabled: !bed.available,
            }))
        : [];
      replaceOptions(bedField, 'Select bed length', bedOptions);
      if (
        preferredBedKey &&
        bedOptions.some((option) => option.value === preferredBedKey && !option.disabled)
      ) {
        bedField.value = preferredBedKey;
      }
      bedField.disabled = bedOptions.length === 0;
      refreshSubmitState();
    };

    const refreshModels = (preferredModelKey = '', preferredBedKey = '') => {
      const modelOptions = entriesForYear()
        .filter((entry) => entry.makeKey === makeField.value)
        .map((entry) => ({ label: entry.model, value: entry.modelKey }))
        .filter(
          (option, index, options) => options.findIndex((candidate) => candidate.value === option.value) === index
        )
        .sort((first, second) => first.label.localeCompare(second.label));

      replaceOptions(modelField, 'Select model', modelOptions);
      if (preferredModelKey && modelOptions.some((option) => option.value === preferredModelKey)) {
        modelField.value = preferredModelKey;
      }
      modelField.disabled = modelOptions.length === 0;
      refreshBeds(preferredBedKey);
    };

    const refreshMakes = (preferredMakeKey = '', preferredModelKey = '', preferredBedKey = '') => {
      const year = selectedYear();
      if (!year || year < approvedYearMin || year > approvedYearMax) {
        replaceOptions(makeField, 'Select make', []);
        replaceOptions(modelField, 'Select model', []);
        replaceOptions(bedField, 'Select bed length', []);
        makeField.disabled = true;
        modelField.disabled = true;
        bedField.disabled = true;
        submitButton.disabled = true;
        setStatus(`Select an approved model year from ${approvedYearMin} through ${approvedYearMax}.`);
        return;
      }

      const makeOptions = entriesForYear()
        .map((entry) => ({ label: entry.make, value: entry.makeKey }))
        .filter(
          (option, index, options) => options.findIndex((candidate) => candidate.value === option.value) === index
        )
        .sort((first, second) => first.label.localeCompare(second.label));

      replaceOptions(makeField, 'Select make', makeOptions);
      if (preferredMakeKey && makeOptions.some((option) => option.value === preferredMakeKey)) {
        makeField.value = preferredMakeKey;
      }
      makeField.disabled = makeOptions.length === 0;
      refreshModels(preferredModelKey, preferredBedKey);

      if (makeOptions.length === 0) {
        setStatus(`No current Silver Bullet fitment is approved for ${year}. Request your truck below.`, 'no-match');
        publish('silver_bullet:fitment_no_match', { source, year, reason: 'year' });
      } else {
        setStatus('Continue with make, model, and bed length.');
      }
    };

    const saveSelection = () => {
      const { entry, bed } = refreshSubmitState();
      const patch = {
        source,
        year: selectedYear(),
        make: entry?.make || (makeField.value ? makeField.selectedOptions[0]?.textContent : ''),
        makeKey: makeField.value,
        model: entry?.model || (modelField.value ? modelField.selectedOptions[0]?.textContent : ''),
        modelKey: modelField.value,
        fitmentKey: entry?.fitmentKey || '',
        productHandle: entry?.productHandle || '',
        bed: bed?.bed || (bedField.value ? bedField.selectedOptions[0]?.textContent : ''),
        bedKey: bedField.value,
        variantId: bed?.variantId || '',
      };
      updateState(patch);
      if (updateUrl) replaceFitmentParameters(state);
      return { entry, bed };
    };

    const markStarted = () => {
      if (started) return;
      started = true;
      publish('silver_bullet:fitment_started', { source });
    };

    yearField.addEventListener('change', () => {
      markStarted();
      refreshMakes();
      saveSelection();
    });

    makeField.addEventListener('change', () => {
      markStarted();
      refreshModels();
      saveSelection();
    });

    modelField.addEventListener('change', () => {
      markStarted();
      refreshBeds();
      saveSelection();
    });

    bedField.addEventListener('change', () => {
      markStarted();
      const { entry, bed } = saveSelection();
      if (entry && bed?.available) {
        setStatus(`${entry.make} ${entry.model}, ${bed.bed} is available.`, 'match');
        publish('silver_bullet:fitment_match_found', {
          source,
          fitment_key: entry.fitmentKey,
          product_handle: entry.productHandle,
          variant_id: variantIdString(bed.variantId),
        });
      } else if (entry && bed) {
        setStatus(`${entry.make} ${entry.model}, ${bed.bed} is not currently available.`, 'no-match');
        publish('silver_bullet:fitment_no_match', {
          source,
          year: selectedYear() || 0,
          reason: 'unavailable-bed',
        });
      }
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const { entry, bed } = saveSelection();
      const year = selectedYear();
      if (!entry || !bed || !year || !bed.available) {
        setStatus('Complete the approved year, make, model, and bed length first.', 'no-match');
        publish('silver_bullet:fitment_no_match', { source, year: year || 0, reason: 'incomplete' });
        return;
      }
      window.location.assign(fitmentUrl(entry, bed, year));
    });

    const initialEntry = findEntry(state);
    const initialBed = findBed(initialEntry, state);
    if (state.year) yearField.value = String(state.year);
    refreshMakes(state.makeKey || initialEntry?.makeKey, state.modelKey || initialEntry?.modelKey, state.bedKey || initialBed?.bedKey);
    if (initialEntry && initialBed) {
      setStatus(`${initialEntry.make} ${initialEntry.model}, ${initialBed.bed} is selected.`, 'match');
    }
    selector.dataset.fitmentInitialized = 'true';
  };

  const initializeProductIntake = (intake) => {
    if (intake.dataset.fitmentStateInitialized === 'true') return;

    const productHandle = intake.dataset.sbProductHandle || '';
    const variantInput = document.getElementById(intake.dataset.productFormId)?.querySelector('input[name="id"]');
    const yearField = intake.querySelector('[data-sb-year-trigger]');
    const modelField = intake.querySelector('[data-sb-vehicle-model]');
    const makeField = intake.querySelector('[data-sb-vehicle-make]');
    const fitmentKeyField = intake.querySelector('[data-sb-fitment-key]');
    const changeLink = intake.querySelector('[data-sb-fitment-change]');
    const requestedModelKey =
      (hasExplicitFitment && explicitState.modelKey) ||
      (storedStateMatchesProduct && storedState.modelKey) ||
      '';
    const compatibleEntry = findEntry({ ...state, productHandle });

    const syncModelMake = () => {
      if (!modelField || !makeField) return;
      const selectedOption = modelField.selectedOptions[0];
      makeField.value = selectedOption?.dataset.vehicleMake || makeField.dataset.defaultValue || '';
    };

    const syncModelOptionsToVariant = () => {
      if (!modelField) return;

      const variantId = variantIdString(variantInput?.value);
      if (!variantId) return;

      const compatibleModelKeys = new Set(
        catalog.entries
          .filter(
            (entry) =>
              entry.productHandle === productHandle &&
              entry.beds.some((bed) => variantIdString(bed.variantId) === variantId)
          )
          .map((entry) => entry.modelKey)
      );
      if (compatibleModelKeys.size === 0) return;

      Array.from(modelField.options).forEach((option) => {
        if (!option.value) return;
        const isCompatible = compatibleModelKeys.has(normalizeKey(option.value));
        option.disabled = !isCompatible;
        option.hidden = !isCompatible;
      });

      if (modelField.selectedOptions[0]?.disabled) modelField.value = '';
      syncModelMake();
    };

    const selectedVariantFitment = () => {
      const variantId = variantInput?.value || '';
      const entry =
        findEntry({
          productHandle,
          make: makeField?.value,
          model: modelField?.value,
          variantId,
        }) || findEntry({ productHandle, variantId });

      return {
        entry,
        bed: findBed(entry, { variantId }),
      };
    };

    const syncYearRangeToVariant = ({ dispatchChange = true } = {}) => {
      syncModelOptionsToVariant();
      const { entry, bed } = selectedVariantFitment();
      if (!yearField || !entry || !bed) return { entry, bed };

      const yearMin = Number(bed.yearMin);
      const yearMax = Number(bed.yearMax);
      if (!Number.isInteger(yearMin) || !Number.isInteger(yearMax) || yearMin > yearMax) {
        return { entry, bed };
      }

      const previousYear = numberOrNull(yearField.value);
      yearField.dataset.yearMin = String(yearMin);
      yearField.dataset.yearMax = String(yearMax);
      yearField.replaceChildren(new Option('Select model year', ''));
      for (let supportedYear = yearMax; supportedYear >= yearMin; supportedYear -= 1) {
        yearField.add(new Option(String(supportedYear), String(supportedYear)));
      }

      if (previousYear && previousYear >= yearMin && previousYear <= yearMax) {
        yearField.value = String(previousYear);
      }

      const yearHint = yearField.closest('[data-sb-intake-field]')?.querySelector('.sb-vehicle-intake__hint');
      if (yearHint) yearHint.textContent = `Approved model years: ${yearMin}-${yearMax}.`;
      intake.dataset.sbVariantId = variantIdString(bed.variantId);

      if (dispatchChange) yearField.dispatchEvent(new Event('change', { bubbles: true }));
      return { entry, bed };
    };

    syncYearRangeToVariant({ dispatchChange: false });

    if (compatibleEntry && compatibleEntry.productHandle === productHandle) {
      const productYearMin = Number(yearField?.dataset.yearMin || 1900);
      const productYearMax = Number(yearField?.dataset.yearMax || catalog.approvedYearMax);
      if (yearField && state.year && state.year >= productYearMin && state.year <= productYearMax) {
        yearField.value = String(state.year);
        yearField.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (modelField && requestedModelKey) {
        const matchingOption = Array.from(modelField.options).find(
          (option) => normalizeKey(option.value) === requestedModelKey && !option.disabled
        );
        if (matchingOption) modelField.value = matchingOption.value;
      }
      syncModelMake();
    }

    const updateChangeLink = () => {
      if (!changeLink) return;
      const baseHref = changeLink.dataset.sbFitmentBaseHref || changeLink.getAttribute('href');
      changeLink.dataset.sbFitmentBaseHref = baseHref;
      const destination = new URL(baseHref, window.location.origin);
      const selectedEntry = findEntry({ ...state, productHandle });
      const selectedBed = findBed(selectedEntry, state);
      if (state.year) destination.searchParams.set('year', String(state.year));
      if (selectedEntry) {
        destination.searchParams.set('make', selectedEntry.make);
        destination.searchParams.set('model', selectedEntry.model);
      }
      if (selectedBed) destination.searchParams.set('bed', selectedBed.bed);
      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) destination.searchParams.set('preview_theme_id', previewThemeId);
      changeLink.href = `${destination.pathname}${destination.search}`;
    };

    const saveProductState = () => {
      const year = numberOrNull(yearField?.value);
      if (modelField && !modelField.value) {
        updateState({
          source: state.source || 'product',
          fitmentKey: '',
          productHandle,
          make: '',
          makeKey: '',
          model: '',
          modelKey: '',
          year,
          bed: '',
          bedKey: '',
          variantId: variantInput?.value || '',
        });
        replaceFitmentParameters(state);
        updateChangeLink();
        return;
      }

      const { entry, bed } = selectedVariantFitment();
      if (
        !entry ||
        !bed ||
        !year ||
        !entrySupportsYear(entry, year) ||
        year < Number(bed.yearMin) ||
        year > Number(bed.yearMax)
      ) {
        updateState({
          source: state.source || 'product',
          fitmentKey: entry?.fitmentKey || '',
          productHandle,
          make: entry?.make || makeField?.value || '',
          makeKey: entry?.makeKey || normalizeKey(makeField?.value),
          model: entry?.model || modelField?.value || '',
          modelKey: entry?.modelKey || normalizeKey(modelField?.value),
          year: null,
          bed: bed?.bed || '',
          bedKey: bed?.bedKey || '',
          variantId: bed?.variantId || variantInput?.value || '',
        });
        if (fitmentKeyField) fitmentKeyField.value = entry?.fitmentKey || '';
        replaceFitmentParameters(state);
        updateChangeLink();
        return;
      }

      updateState({
        source: state.source || 'product',
        fitmentKey: entry.fitmentKey,
        productHandle,
        make: entry.make,
        makeKey: entry.makeKey,
        model: entry.model,
        modelKey: entry.modelKey,
        year,
        bed: bed.bed,
        bedKey: bed.bedKey,
        variantId: bed.variantId,
      });
      if (fitmentKeyField) fitmentKeyField.value = entry.fitmentKey;
      replaceFitmentParameters(state);
      updateChangeLink();
    };

    modelField?.addEventListener('change', () => {
      syncModelMake();
      saveProductState();
    });
    yearField?.addEventListener('change', saveProductState);
    variantInput?.addEventListener('change', () =>
      requestAnimationFrame(() => {
        syncYearRangeToVariant();
        saveProductState();
      })
    );

    if (compatibleEntry && compatibleEntry.productHandle === productHandle) saveProductState();
    updateChangeLink();

    intake.dataset.fitmentStateInitialized = 'true';
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-sb-fitment-selector]').forEach(initializeSelector);
    scope.querySelectorAll('[data-sb-product-fitment]').forEach(initializeProductIntake);
  };

  window.SilverBullet = window.SilverBullet || {};
  window.SilverBullet.fitment = {
    catalog,
    getState: () => ({ ...state }),
    updateState,
    replaceFitmentParameters,
    publish,
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeAll();
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
